import React, { useState, useEffect, useContext } from "react";
import { getProduct, updateCart } from "../../util/ApiService";
import "./ProductDetails.css";
import { Truck, RotateCcw } from "lucide-react";
import RecommendedProducts from "./RecommandProducts";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { LanguageContext } from "../../context/LanguageContext";
import AddReviews from "./AddReviews";
import AdvancedLoadingScreen from "../Loader/AdvancedLoadingScreen";

const ProductDetails = () => {
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const user = JSON.parse(localStorage.getItem("users"));
  const uuid = sessionStorage.getItem("uuid");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { id } = useParams();

  const { currentLang: selectedLanguage } = useContext(LanguageContext);

  useEffect(() => {
    const user = localStorage.getItem("user");

    if (!user) {
      return;
    }

    const storedWishlist = localStorage.getItem("wishlist");

    if (storedWishlist) {
      setWishlist(JSON.parse(storedWishlist));
    }
  }, []);

  const handleCartUpdate = (item, qty) => {
    const params = {
      sku: item.sku,
      qty: qty,
      action: "I",
      userId: user?.email || uuid,
      productId: product?.id,
    };
    updateCart(params)
      .then((response) => {
        enqueueSnackbar("Product added to cart!", { variant: "success" });
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const addToCart = () => {
    const user = localStorage.getItem("users");
    if (!user) {
      enqueueSnackbar("Please log in to add products to your cart!", {
        variant: "error",
      });
      return;
    }
    handleCartUpdate(currentVariant, quantity);
    setCart([...cart, { ...currentVariant, quantity }]);
  };

  const addToWishlist = () => {
    const user = localStorage.getItem("users");

    if (!user) {
      enqueueSnackbar("Please log in to add products to your wishlist!", {
        variant: "error",
      });
      return;
    }
    const updatedWishlist = [...wishlist, { ...product }];
    setWishlist(updatedWishlist);
    localStorage.setItem("wishlist", JSON.stringify(updatedWishlist));
    enqueueSnackbar("Product added to wishlist!", { variant: "success" });
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const data = await getProduct(id, selectedLanguage);
        console.log("Fetched Product Data:", data.data);
        setProduct(data.data);

        if (data.data?.variants?.length > 0) {
          setSelectedColor(data.data.variants[0].color);
          setSelectedSize(data.data.variants[0].size);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      }
    };

    fetchProduct();
  }, [selectedLanguage]);

  if (!product) return <AdvancedLoadingScreen />;

  // Get available sizes based on selected color
  const availableSizes = [
    ...new Set(
      product.variants
        .filter((v) => v.color === selectedColor)
        .map((v) => v.size)
    ),
  ];

  // Get available colors based on selected size
  const availableColors = [
    ...new Set(
      product.variants
        .filter((v) => v.size === selectedSize)
        .map((v) => v.color)
    ),
  ];

  // Get the current selected variant
  const currentVariant =
    product.variants.find(
      (v) => v.color === selectedColor && v.size === selectedSize
    ) || product.variants[0];

  // Calculate discount amount and percentage
  const calculateDiscount = (variant) => {
    const discountAmount = variant.actualPrice - variant.sellingPrice;
    const discountPercentage = Math.round(
      (discountAmount / variant.actualPrice) * 100
    );
    return {
      amount: discountAmount,
      percentage: discountPercentage,
    };
  };

  return (
    <>
      <div className="product-container">
        <div className="product-grid">
          {/* Image Section */}
          <div className="image-gallery">
            <div className="thumbnails">
              {product.images?.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`thumbnail-btn ${
                    selectedImage === index ? "active" : ""
                  }`}
                >
                  <img
                    src={img}
                    alt={`View ${index + 1}`}
                    className="thumbnail-img"
                  />
                </button>
              ))}
            </div>
            <div className="main-image">
              <img
                src={product.images?.[selectedImage] || "/placeholder.jpg"}
                alt={product.name}
                className="product-img"
              />
            </div>
          </div>

          {/* Product Details */}
          <div className="product-info">
            <h1 className="product-title-d">{product.name}</h1>
            <p className="product-description">{product.description}</p>

            {/* Price Display */}
            {currentVariant && (
              <div className="price-container">
                <span className="selling-price">
                  $
                  {currentVariant.sellinglPrice -
                    (
                      (currentVariant.sellinglPrice *
                        product.discountPercentage) /
                      100
                    ).toFixed(2)}
                </span>
                <span className="original-price">
                  ₹{currentVariant.sellinglPrice}
                </span>
                <span className="discount-d">
                  {product.discountPercentage}% off
                </span>
                <span className="discount-amount">
                  You save: $
                  {(
                    (currentVariant.sellinglPrice *
                      product.discountPercentage) /
                    100
                  ).toFixed(2)}
                </span>
              </div>
            )}

            {/* Rest of the component remains the same */}
            {/* Color Options */}
            {product.variants.length > 1 && (
              <div className="variant-section">
                <h3 className="variant-title">Color:</h3>
                <div className="color-options">
                  {availableColors.map((color) => (
                    <button
                      key={color}
                      style={{ backgroundColor: color }}
                      onClick={() => setSelectedColor(color)}
                      className={`color-btn ${
                        selectedColor === color ? "active" : ""
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Size Options */}
            {product.variants.length > 1 && (
              <div className="variant-section">
                <h3 className="variant-title">Size:</h3>
                <div className="size-options">
                  {availableSizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`size-btn ${
                        selectedSize === size ? "active" : ""
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="purchase-actions">
              <button className="add-to-cart-btn" onClick={addToCart}>
                Add to Cart
              </button>
              <button className="add-to-wishlist-btn" onClick={addToWishlist}>
                Add to Wishlist
              </button>
            </div>

            <div className="delivery-info">
              <div className="delivery-item">
                <Truck className="delivery-icon" />
                <div className="delivery-text">
                  <div className="delivery-title">Free Delivery</div>
                  <div className="delivery-subtitle">
                    Enter your postal code for Delivery Availability
                  </div>
                </div>
              </div>
              <div className="delivery-item">
                <RotateCcw className="delivery-icon" />
                <div className="delivery-text">
                  <div className="delivery-title">Return Delivery</div>
                  <div className="delivery-subtitle">
                    Free 30 Days Delivery Returns.{" "}
                    <button className="details-link">Details</button>
                  </div>
                </div>
              </div>
            </div>

            {product.specifications?.length > 0 && (
              <div className="product-specifications">
                <h3 className="spec-title">Product Details</h3>

                {product.specifications.map((spec, index) => (
                  <div key={index} className="spec-category">
                    {spec.additionalInfo && (
                      <h4 className="spec-category-title">
                        {spec.additionalInfo}
                      </h4>
                    )}
                    <dl className="spec-grid">
                      {Object.entries(spec.additionalSpecs || {}).map(
                        ([key, value]) => (
                          <div key={key} className="spec-item">
                            <dt className="spec-key">{key}</dt>
                            <dd className="spec-value">{value}</dd>
                          </div>
                        )
                      )}
                    </dl>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <AddReviews product={product} />
        <div>
          <h4 style={{ fontSize: "25px", fontWeight: 700 }}>
            Customers Also Bought
          </h4>
          <RecommendedProducts />
        </div>
      </div>
    </>
  );
};

export default ProductDetails;
